import { downloadBlob } from '@/components/utils/download';
import { TrackingType } from '@/types';

export const KARAS_RED = '#8B3A3A';
export const REPORT_BORDER = '#CBD5E1';
export const REPORT_GRID = '#D8DDE6';
export const REPORT_TEXT = '#111827';
export const REPORT_SUBTLE = '#6B7280';
export const REPORT_HEADER_BG = '#F3F4F6';
export const REPORT_FONT = 'Helvetica, Arial, sans-serif';

export const MM_PER_IN = 25.4;

export const toDisplay = (mm: number, unit: 'mm' | 'in') =>
  unit === 'mm' ? Number(mm.toFixed(2)) : Number((mm / MM_PER_IN).toFixed(4));

export const fromDisplay = (value: number, unit: 'mm' | 'in') =>
  unit === 'mm' ? value : Number((value * MM_PER_IN).toFixed(4));

export const newRequestId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const deepLinkPath = (
  link: TrackingType.DeepLink | null | undefined,
) => {
  if (!link) return null;
  const params = new URLSearchParams();
  Object.entries(link.query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '')
      params.set(key, String(value));
  });
  const query = params.toString();
  return `${link.route}${query ? `?${query}` : ''}`;
};

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Print a ready PDF blob through the browser's print dialog.
 *
 * Must be called from a user gesture with the blob already loaded so the
 * browser keeps user activation. Returns false when the platform cannot print
 * an embedded PDF (iOS/Android), so the caller can offer Open PDF instead.
 */
export const printPdfBlob = (blob: Blob): boolean => {
  const ua = navigator.userAgent;
  const mobile =
    /iPhone|iPad|iPod|Android/i.test(ua) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
  if (mobile) return false;
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.title = 'Print document';
  Object.assign(frame.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
  });
  frame.src = url;
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } finally {
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
        frame.remove();
      }, 60_000);
    }
  };
  document.body.appendChild(frame);
  return true;
};

export const openPdfBlob = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  // 'noopener' makes window.open return null, so detach the opener explicitly.
  const opened = window.open(url, '_blank');
  if (opened) opened.opener = null;
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
  return Boolean(opened);
};

export const saveBlob = (blob: Blob, filename: string) =>
  downloadBlob({ blob, filename });

export const sectionKindLabel: Record<string, string | undefined> = {
  aggregate: 'Aggregate',
  restricted: 'Restricted',
  unavailable: 'Source unavailable',
  manual: 'Entered in Tracking',
};
